import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Loader2, AlertCircle, Eye, EyeOff } from 'lucide-react';
import BrandLogo from '../components/brand/BrandLogo';
import { useAuth } from '../lib/auth';

// Feb 2026 — team-member login. Uses the shared AuthProvider.teamLogin
// helper so the AuthCtx has a coherent identity BEFORE we navigate into
// the dashboard (no more falling into the seller-login redirect trap).
// Landing target = /dashboard/orders when Orders is assigned, else the
// first assigned department slug, else /dashboard (route guard will then
// push to the first allowed page).
const DEPT_ROUTE_MAP = {
  'Dashboard':         '/dashboard',
  'Products':          '/dashboard/products',
  'Categories':        '/dashboard/categories',
  'Catalog Broadcast': '/dashboard/broadcast',
  'WhatsApp':          '/dashboard/whatsapp',
  'Orders':            '/dashboard/orders',
  'Customers':         '/dashboard/customers',
  'Customer Support':  '/dashboard/support',
  'AI Assistant':      '/dashboard/ai',
  'Payments':          '/dashboard/payments',
  'Shipping':          '/dashboard/shipping',
  'Reports & Analytics': '/dashboard/analytics',
  'Notifications':     '/dashboard/notifications',
  'Settings':          '/dashboard/settings',
};

export default function TeamLogin() {
  const { teamLogin } = useAuth();
  const nav = useNavigate();
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const submit = async (e) => {
    e.preventDefault(); setErr(''); setBusy(true);
    try {
      const data = await teamLogin(phone.trim(), password);
      const deps = data?.team_member?.departments || [];
      // Prefer Orders, else the first assigned department, else /dashboard.
      const target = deps.includes('Orders')
        ? '/dashboard/orders'
        : (deps[0] && DEPT_ROUTE_MAP[deps[0]]) || '/dashboard';
      nav(target, { replace: true });
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Login failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f7f2fb] to-white flex items-center justify-center p-4" data-testid="team-login-page">
      <div className="w-full max-w-sm">
        <Link to="/" className="flex items-center justify-center mb-6"><BrandLogo height={32} /></Link>
        <div className="bg-white rounded-2xl shadow-sm border border-fab-primary-lighter p-6">
          <div className="text-[10px] uppercase tracking-widest text-fab-text-muted font-semibold text-center">Team Member Login</div>
          <h1 className="font-display font-extrabold text-fab-text text-[22px] text-center mt-1">Sign in to your team</h1>
          <form onSubmit={submit} className="mt-5 space-y-3">
            <div>
              <label className="text-[12px] font-semibold">Mobile Number</label>
              <input type="tel" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                placeholder="+91 98765 43210"
                className="mt-1 w-full px-3 py-2 rounded-lg border border-fab-primary-lighter text-[13px]"
                data-testid="team-login-phone" />
            </div>
            <div>
              <label className="text-[12px] font-semibold">Password</label>
              <div className="relative mt-1">
                <input
                  type={showPw ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-3 pr-10 py-2 rounded-lg border border-fab-primary-lighter text-[13px]"
                  data-testid="team-login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  className="absolute inset-y-0 right-0 px-2.5 text-fab-text-muted hover:text-fab-text"
                  aria-label={showPw ? 'Hide password' : 'Show password'}
                  data-testid="team-login-toggle-password"
                >
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            {err && (
              <div className="text-[12px] text-red-700 bg-red-50 border border-red-200 rounded p-2 flex items-start gap-2" data-testid="team-login-error">
                <AlertCircle className="w-4 h-4 mt-0.5" /> {err}
              </div>
            )}
            <button type="submit" disabled={busy}
              className="w-full py-2.5 rounded-lg bg-fab-primary text-white font-semibold text-[13px] inline-flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="team-login-submit"
            >
              {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Signing in…</> : 'Login'}
            </button>
          </form>
        </div>
        <div className="text-[11px] text-fab-text-muted text-center mt-3">Powered by <span className="font-semibold">FABTEXS AI</span></div>
      </div>
    </div>
  );
}
